import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, ROLE_LABELS } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import Footer from "@/components/Footer";
import HelpBot from "@/components/HelpBot";
import GuidedTour from "@/components/GuidedTour";
import Notifications from "@/components/Notifications";
import { usePermissions } from "@/lib/perms";
import AthleteTour from "@/components/AthleteTour";
import { ChatBadge } from "@/components/ChatBadge";
import JobsTray from "@/components/JobsTray";
import { LayoutGrid, ClipboardList, UserPlus, LogOut, User, ShieldCheck, Coins, School, Menu, X, PanelLeftClose, PanelLeftOpen, PlayCircle, Trophy, Settings } from "lucide-react";

// roles: undefined = everyone signed in; array = only those roles
const NAV = [
  { to: "/coach", id: "coach-desk", label: "My Coaching Day", icon: ClipboardList, roles: ["coach"] },
  { to: "/admin", id: "admin", label: "Academy Operations", icon: LayoutGrid, roles: ["admin"] },

  { to: "/command-center", id: "home", label: "Command Centre", icon: LayoutGrid, roles: ["admin", "coach"], section: "squad" },
  { to: "/weekly-reports", id: "weekly-reports", label: "Weekly Reports", icon: ClipboardList, roles: ["admin","athlete"] },
  { to: "/my-schedule", id: "my-schedule", label: "My Schedule & Trips", icon: ClipboardList, roles: ["admin","athlete"] },
  { to: "/athlete", id: "athlete", label: "Athlete Workspace", icon: User },
  { to: "/athlete?tab=training", id: "my-training", label: "My Training", icon: ClipboardList, roles: ["athlete"] },
  { to: "/athlete?tab=strategy", id: "my-strategy", label: "My Strategy", icon: Trophy, roles: ["athlete"] },
  { to: "/athlete?tab=chat", id: "my-chat", label: "Coach Messages", icon: ClipboardList, roles: ["athlete"] },
  { to: "/payments", id: "payments", label: "Academy Payments", icon: Coins, roles: ["athlete"] },
  { to: "/join", id: "join", label: "Join Academy", icon: School, roles: ["coach", "athlete"], hideInAcademy: true },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/my-data", id: "my-data", label: "Your Data", icon: ShieldCheck, roles: ["athlete"] },
];

function CreditsBadge() {
  const [c, setC] = useState(null);
  useEffect(() => { const load = () => api.get("/credits/balance").then((r) => setC(r.data.credits)).catch(() => {}); load(); window.addEventListener("credits-changed", load); return () => window.removeEventListener("credits-changed", load); }, []);
  if (c == null) return null;
  return (
    <NavLink to="/settings/billing" data-testid="nav-credits" className="flex items-center justify-between text-xs rounded-lg border border-border px-3 py-2 mb-3 hover:bg-surface transition-colors">
      <span className="flex items-center gap-1.5 text-muted-foreground"><Coins className="w-3.5 h-3.5" /> Credits</span>
      <span className="font-heading font-bold tabular-nums" data-testid="nav-credit-balance">{c}</span>
    </NavLink>
  );
}

function SidebarContent({ user, logout, isStaff, canBill, onNavigate, collapsed, onToggleCollapse, onStartTour }) {
  const loc = useLocation();
  // Command Center and Squad Simulator share the "/" path (tabs), so decide active state on the query too.
  const isOn = (to) => {
    const [path, q] = to.split("?");
    if (loc.pathname !== (path || "/")) return false;
    return q ? loc.search.includes(q) : ["/admin", "/operations"].includes(path) || !new URLSearchParams(loc.search).get("tab");
  };
  const { can } = usePermissions();
  const items = NAV.filter((n) => (!n.staffOnly || isStaff) && (!n.orgOnly || user.__isOrg)
    && (!n.roles || n.roles.includes(user?.role)) && (!n.section || can(n.section)) && !(n.hideInAcademy && user?.in_academy));
  return (
    <>
      <div className="hidden lg:flex items-center px-4 h-16 border-b border-border shrink-0 gap-2">
        {!collapsed && <div className="flex-1 min-w-0"><Wordmark /></div>}
        {onToggleCollapse && (
          <button data-testid="sidebar-toggle" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"}
            className={`p-1.5 hover:bg-surface transition-colors ${collapsed ? "mx-auto" : ""}`}>
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        )}
      </div>
      <nav className="flex-1 py-3 overflow-y-auto">
        {isStaff && (
          <NavLink to="/add-athlete" onClick={onNavigate} data-testid="nav-add-athlete"
            title="Add / Invite athlete"
            className={`mx-4 mb-3 flex items-center ${collapsed ? "justify-center" : "justify-center gap-2"} rounded-full bg-foreground text-background px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors`}>
            <UserPlus className="w-4 h-4" /> {!collapsed && "Add / Invite athlete"}
          </NavLink>
        )}
        {items.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            data-testid={`nav-${id || (to === "/" ? "home" : to.slice(1))}`}
            className={() =>
              `mx-3 mb-0.5 flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3.5"} rounded-lg py-2.5 text-sm transition-colors ${
                isOn(to) ? "bg-surface font-medium text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-surface"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4 shrink-0">
        <ChatBadge prefix={onNavigate ? "drawer" : "sidebar"}/>
        {user?.is_demo && (
          <NavLink to="/membership" onClick={onNavigate} data-testid="nav-join-now" title="Join now"
            className={`w-full mb-3 flex items-center ${collapsed ? "justify-center" : "justify-center gap-2"} bg-brand-magenta text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity`}>
            <Trophy className="w-4 h-4" /> {!collapsed && "Join now"}
          </NavLink>
        )}
        <button data-testid="open-tour" onClick={onStartTour}
          title="Show me how to use this app"
          className={`w-full mb-3 flex items-center ${collapsed ? "justify-center" : "gap-2"} border border-foreground py-2 text-xs hover:bg-surface transition-colors`}>
          <PlayCircle className="w-4 h-4" /> {!collapsed && "Show me how"}
        </button>
        {canBill && !collapsed && <CreditsBadge />}
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} mb-3`}>
          {!collapsed && <div className="order-last ml-auto"><Notifications placement="up-right" /></div>}
          <img src={user?.picture} alt="" className="w-9 h-9 rounded-full grayscale object-cover border border-border shrink-0"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          )}
        </div>
        <button data-testid="logout-btn" onClick={logout}
          title="Sign out"
          className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} text-xs text-muted-foreground hover:text-foreground transition-colors py-2`}>
          <LogOut className="w-4 h-4" /> {!collapsed && "Sign out"}
        </button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("xps.sidebar.collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapse = () => setCollapsed((c) => {
    const nv = !c;
    try { localStorage.setItem("xps.sidebar.collapsed", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  const isStaff = user?.role !== "athlete";
  const isOrg = !user?.is_demo && ((user?.email || "").toLowerCase().endsWith("@trifitindia.com") || user?.role === "admin" || user?.role === "owner");
  const canBill = ["admin", "owner", "coach", "demo"].includes(user?.role);
  const [tour, setTour] = useState(false);
  const u = { ...(user || {}), __isOrg: isOrg };

  useEffect(() => { setOpen(false); }, [children]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* mobile / tablet top bar */}
      <div className="app-topbar lg:hidden sticky top-0 z-50 h-14 border-b border-border bg-background flex items-center gap-3 px-4">
        <button data-testid="mobile-nav-toggle" aria-label="Open navigation" onClick={() => setOpen(true)}
          className="p-1.5 -ml-1.5 hover:bg-surface transition-colors">
          <Menu className="w-5 h-5" />
        </button>
        <button onClick={() => navigate("/")} className="min-w-0"><Wordmark size="text-base" logoClass="w-5 h-5" /></button>
        <div className="ml-auto flex items-center gap-1"><ChatBadge prefix="mobile"/><Notifications /></div>
        {user?.is_demo && (
          <button data-testid="mobile-join-now" onClick={() => navigate("/membership")}
            className="text-[10px] uppercase tracking-widest bg-brand-magenta text-white px-2 py-1 font-bold">Join now</button>
        )}
        {user?.is_demo && <span data-testid="demo-badge" className="text-[10px] uppercase tracking-widest border border-brand-magenta text-brand-magenta px-1.5 py-0.5">Demo</span>}
      </div>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[60] flex" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-[17rem] max-w-[85vw] h-full bg-background border-r border-border flex flex-col">
            <div className="flex items-center h-14 px-4 border-b border-border shrink-0">
              <Wordmark size="text-base" logoClass="w-5 h-5" />
              <button data-testid="mobile-nav-close" aria-label="Close navigation" onClick={() => setOpen(false)} className="ml-auto p-1.5 hover:bg-surface">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent user={u} logout={logout} isStaff={isStaff} canBill={canBill} onNavigate={() => setOpen(false)} collapsed={false} onStartTour={() => { setOpen(false); setTour(true); }} />
          </aside>
        </div>
      )}

      {/* desktop sidebar (collapsible) */}
      <aside className={`hidden lg:flex ${collapsed ? "w-16" : "w-60"} border-r border-border flex-col fixed top-0 left-0 h-screen bg-background z-40 transition-[width] duration-200`}>
        <SidebarContent user={u} logout={logout} isStaff={isStaff} canBill={canBill} collapsed={collapsed} onToggleCollapse={toggleCollapse} onStartTour={() => setTour(true)} />
      </aside>

      <main className={`${collapsed ? "lg:ml-16" : "lg:ml-60"} min-h-screen flex flex-col overflow-x-hidden transition-[margin] duration-200`}>
        <div className="flex-1 w-full max-w-[1800px]">{children}</div>
        <Footer compact />
      </main>
      <HelpBot />
      <JobsTray />
      {isStaff
        ? <GuidedTour open={tour} isDemo={!!user?.is_demo} onClose={() => setTour(false)} />
        : <AthleteTour open={tour} onClose={() => setTour(false)} />}
    </div>
  );
}
